import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { AxiosError, AxiosResponse } from 'axios';
import { AddTasksPayload, BulkDeal, CategoryTask, Deal, EditDealRequest, EditTasksPayload } from 'types/Contract';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { UploadedFile } from 'models/Files';
import { FollowContractRecord, MultiDealRequestPayload } from '../models/Deal';
import { BASE_URL, BASE_URL_V2, USE_NEW_DEAL_API } from 'shared/constants';

export const useContractApi = () => {
  const { axios } = useAxios(BASE_URL);
  const { axios: axiosV2 } = useAxios(BASE_URL_V2);
  /**
   * Fetch the logged-in user's company contracts
   */
  const fetchMyCompanyContracts = async () => {
    try {
      const res = await axios.get(`/secure/contracts`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(
        new Error(res.data?.message || 'Error occurred while fetching the user`s company contracts')
      );
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch contract summary
   * @param contract_id
   */
  const fetchContractSummary = async (contract_id: number) => {
    try {
      const res = await axios.get(`/api/contract/${contract_id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while fetching the contract summary'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch contract summary
   * @param contract_id
   */
  const fetchDealContacts = async (contract_id: number) => {
    try {
      const res = await axios.get(`/api/contract/${contract_id}/contacts`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while fetching deal contracts'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch contract comps
   * @param {number} contract_id
   * @param {number} years
   */
  const fetchDealComps = async (contract_id: number, years: number) => {
    try {
      const res = await axios.get(`/api/contract/${contract_id}/comps?years=${years}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while fetching the contract comps'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Returns a Prisma structured JSON object
   * @param {number} contract_id
   */
  const fetchContract = async (contract_id: number) => {
    try {
      const res = await axios.get(`/secure/p/contract/${contract_id}`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Returns deal by contract id or deal id (Salesforce id).
   * When VITE_APP_USE_NEW_DEAL_API is true, uses Nest GET /deals/:deal_id/detail; otherwise v1 GET /secure/deal/:id.
   * @param {number | string} id - Contract ID or Salesforce deal id
   */
  const fetchDeal = async (id: number | string) => {
    try {
      const url = USE_NEW_DEAL_API ? `/deals/${id}/detail` : `/secure/deal/${id}`;
      const client = USE_NEW_DEAL_API ? axiosV2 : axios;
      const res: AxiosResponse<any, any> = await client.get(url);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(new Error(res?.data?.message || 'Error occurred while fetching deal'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch Contract Renewal Notifications
   * @param {number} id
   */
  const fetchContractRenewalNotifications = async (id: number) => {
    try {
      const res: AxiosResponse<any, any> = await axios.get(`/secure/contract/${id}/renewal/notifications`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data?.message || 'Error occurred while fetching the user`s company contracts'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const addDeal = async (deal: Deal, profile_id?: number) => {
    try {
      const url = `/contracts/deal`;
      const response = await axios.post(url, deal, { params: { profile_id }, baseURL: BASE_URL_V2 });
      console.log('data', response.data);
      if (response.data) return response.data;
      return Promise.reject(Error(response.data?.message || 'Error occurred while adding deal'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const editDeal = async (dealId: number, deal: EditDealRequest) => {
    try {
      const url = `/api/deal/${dealId}`;
      const { data } = await axios.patch(url, deal);
      return data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Returns a Prisma structured JSON object
   * @param formData
   */
  const fileUpload = async (formData: any) => {
    try {
      const res: AxiosResponse<any, any> = await axios.post(`/api/files/upload`, formData, {
        transformRequest: () => formData
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while file upload'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Remove File
   * @param key
   */
  const removeFile = async (key: string) => {
    try {
      const res: AxiosResponse<any, any> = await axios.delete(`/api/file`, {
        params: { key }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while removing file'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const removeFiles = async (keys: string[]) => {
    try {
      const res: AxiosResponse<any, any> = await axios.delete(`/api/files`, {
        data: keys
      });
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(new Error(res?.data?.message || 'Error occurred while removing files'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const deleteDeal = async (id: number) => {
    try {
      const res: AxiosResponse<any, any> = await axios.delete(`/api/deal/${id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while deleting deal'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getBlobFromFileURL = async (fileUrl: string): Promise<Blob | undefined> => {
    try {
      const response = await fetch(fileUrl);
      if (response.ok) {
        return response.blob();
      }
      return Promise.reject(Error(`Failed to fetch file: ${response.statusText}`));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const removeContractFile = async (id: number, key: string) => {
    try {
      const res: AxiosResponse<any, any> = await axios.delete(`/api/deal/${id}/file`, {
        params: { key }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while deleting contract file'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Attach upload files to contract record
   * @param id
   * @param data
   */
  const attachContractFiles = async (id: number, data: UploadedFile[]) => {
    try {
      const res: AxiosResponse<any, any> = await axios.post(`/api/deal/${id}/files/attach`, data);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while attaching contract file'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const parseBulkContractFile = async (formData: any) => {
    try {
      const res: AxiosResponse<any, any> = await axios.post(`/api/deals/upload/parse`, formData, {
        transformRequest: () => formData
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while parsing bulk contract file'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const addBulkDeal = async (deals: BulkDeal[]) => {
    try {
      const url = `/api/deals`;
      const { data } = await axios.post(url, deals);
      return data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateDealMetadata = async (id: number, data: any) => {
    try {
      const res = await axios.patch(`/benchmark-report-request/deal/${id}/metadata`, data, {
        baseURL: BASE_URL_V2
      });
      if (res?.status == 200) {
        return res?.data;
      }
      return Promise.reject(new Error(res?.data?.message || 'Failed to update deal metadata'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getCategoryDefaultTasks = async (): Promise<CategoryTask[]> => {
    try {
      const url = `/api/tasks/category_default`;
      const res = await axios.get(url);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(new Error(res?.data?.message || 'Failed to fetch category default tasks'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetch deal notes
   * @param contract_id
   */
  /**
   * Retrieves contract notes for a given contract ID.
   * @param {number} contractId - The ID of the contract.
   * @returns {Promise<any[]>} Resolves to an array of contract notes.
   * @throws {Error} If the API request fails.
   */
  const fetchDealNotes = async (contractId: number) => {
    try {
      const res = await axiosV2.get(`/note/deal/${contractId}`);
      if (res.data?.statusCode === 200 || res.status === 200) {
        return res.data?.data || res.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while fetching deal notes'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const getRequestAccess = async (contract_id: number) => {
    try {
      const res = await axios.get(`/api/deal/documents/${contract_id}/access_request`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while fetching request access'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const sendRequestAccess = async (payload: { contract_id: number }) => {
    try {
      const res = await axios.post(`/api/deal/documents/access_request`, payload);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while sending request access'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const notifyClient = async (payload: { contract_id: number }) => {
    try {
      const res = await axios.post(`/api/deal/documents/notify_access_grant`, payload);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while sending access grant notification'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const addTasks = async (dealId: number, payload: AddTasksPayload[]) => {
    try {
      const res = await axios.post(`/api/contract/${dealId}/tasks`, payload);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while adding tasks'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Create a contract note for a given deal.
   * @param {number} contractId - The ID of the deal to create the contract note for.
   * @param {object} dealNote - The data for creating the contract note.
   * @param {string} dealNote.note - The note content.
   * @param {number} [dealNote.parent_id] - Optional parent note ID for replies.
   * @param {boolean} dealNote.isVisibleToNpi - Whether the note is visible to NPI.
   * @returns {Promise<any>} The created contract note.
   * @throws {Error} If the API request fails.
   */
  const createDealNote = async (
    contractId: number,
    dealNote: { note: string; parent_id?: number; isVisibleToNpi: boolean }
  ) => {
    try {
      const url = `/note/deal/${contractId}`;
      // Transform camelCase to snake_case for backend compatibility
      const payload = {
        note: dealNote.note,
        parent_id: dealNote.parent_id,
        is_visible_to_npi: dealNote.isVisibleToNpi
      };
      const { data } = await axiosV2.post(url, payload);
      return data?.data || data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Update a contract note.
   * @param {number} contractId - The ID of the contract.
   * @param {number} noteId - The ID of the contract note to update.
   * @param {object} payload - The data to update the contract note with.
   * @param {string} payload.note - The updated note content.
   * @param {boolean} payload.isVisibleToNpi - Whether the note is visible to NPI.
   * @returns {Promise<any>} A promise that resolves to updated contract note data.
   * @throws {Error} If the API request fails.
   */
  const updateDealNote = async (
    contractId: number,
    noteId: number,
    payload: { note: string; isVisibleToNpi: boolean }
  ) => {
    try {
      const url = `/note/deal/${contractId}/note/${noteId}`;
      // Transform camelCase to snake_case for backend compatibility
      const updatePayload = {
        note: payload.note,
        is_visible_to_npi: payload.isVisibleToNpi
      };
      const { data } = await axiosV2.patch(url, updatePayload);
      return data?.data || data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Deletes a contract note.
   * @param {number} contractId - The ID of the contract.
   * @param {number} noteId - The ID of the note to delete.
   * @returns {Promise<any>} A promise that resolves to data of the deleted note.
   * @throws {Error} If the API request fails.
   */
  const deleteDealNote = async (contractId: number, noteId: number) => {
    try {
      const url = `/note/deal/${contractId}/note/${noteId}`;
      const { data } = await axiosV2.delete(url);
      return data?.data || data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Update deal task
   * @param dealId
   * @param taskId
   * @param data
   */
  const updateTask = async (dealId: number, taskId: number, data: EditTasksPayload) => {
    try {
      const res = await axios.patch(`api/contract/${dealId}/task/${taskId}`, data);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while updating task'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Delete deal task
   * @param contractId
   * @param taskId
   */
  const deleteDealTask = async (contractId: number, taskId: number) => {
    try {
      const url = `/api/contract/${contractId}/task/${taskId}`;
      const { data } = await axios.delete(url);
      return data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const addMultipleDeal = async (deal: any, profile_id?: number) => {
    try {
      const url = `/api/deals`;
      const { data } = await axios.post(url, deal, { params: { profile_id } });
      return data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const fetchContractsForNotifications = async (profile_id?: number) => {
    try {
      const res = await axios.get(`/api/notifications/contracts`, {
        params: { profile_id }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(
        new Error(res.data?.message || 'Error occurred while fetching the contracts for notifications')
      );
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Update contract comments to customer
   * @param {string} contractId - The contract ID (deal ID)
   * @param {string} commentsData - The comments to update
   * @returns {Promise<any>} The updated contract comments
   */
  const updateContractComments = async (contractId: string, commentsData: string) => {
    try {
      const url = `/salesforce/deal/${contractId}/comments`;
      const payload = { Comments_To_Customer__c: commentsData };
      const config = { baseURL: BASE_URL_V2 };
      const res = await axios.patch(url, payload, config);

      if (res?.status == 200) {
        return res?.data;
      }
      return Promise.reject(new Error(res?.data?.message || 'Failed to update contract comments'));
    } catch (e) {
      console.error('API call error:', e);
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Export Vendor/Product Bill of Materials
   *
   * Calls v2 endpoint to generate a Vendor/Product export file. Returns a Blob suitable
   * for direct download. Optionally accepts a profileId for switched profiles.
   * @param {number} [profileId] - Optional switched profile id
   * @returns {Promise<Blob | undefined>} A blob containing the exported file, if successful
   */
  const exportVendorProduct = async (profileId?: number): Promise<Blob | undefined> => {
    try {
      const res = await axios.get(`/contracts/bom/vendor-product-export`, {
        baseURL: BASE_URL_V2,
        params: profileId ? { profileId } : undefined,
        responseType: 'blob'
      });
      if (res?.status === 200) {
        return res?.data as Blob;
      }
      return Promise.reject(new Error(res?.statusText || 'Failed to export vendor/product data'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Returns a Prisma structured JSON object
   * @param formData
   */
  const fileUploadUnsafe = async (formData: any) => {
    try {
      const res: AxiosResponse<any, any> = await axios.post(`/api/files/upload`, formData, {
        transformRequest: () => formData
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(new Error(res.data?.message || 'Error occurred while file upload'));
    } catch (e) {
      await AxiosErrorHandler(e);
    }
  };

  return {
    fetchMyCompanyContracts,
    fetchContractSummary,
    fetchDealContacts,
    fetchDealComps,
    fetchContract,
    fetchDeal,
    fetchContractRenewalNotifications,
    addDeal,
    editDeal,
    fileUpload,
    removeFile,
    removeFiles,
    deleteDeal,
    getBlobFromFileURL,
    removeContractFile,
    attachContractFiles,
    parseBulkContractFile,
    addBulkDeal,
    updateDealMetadata,
    getCategoryDefaultTasks,
    fetchDealNotes,
    getRequestAccess,
    sendRequestAccess,
    notifyClient,
    addTasks,
    createDealNote,
    updateDealNote,
    deleteDealNote,
    updateTask,
    deleteDealTask,
    addMultipleDeal,
    fetchContractsForNotifications,
    fileUploadUnsafe,
    updateContractComments,
    exportVendorProduct
  };
};

export const useContractsForNotifications = (
  profile_id?: number
): UseQueryResult<FollowContractRecord[], AxiosError> => {
  const { fetchContractsForNotifications } = useContractApi();
  return useQuery({
    queryKey: ['contracts', profile_id],
    queryFn: () => fetchContractsForNotifications(profile_id)
  });
};

export const useAddMultipleDeal = (config = {}) => {
  const { addMultipleDeal } = useContractApi();
  return useMutation({
    mutationFn: async ({ data, profile_id }: { data: MultiDealRequestPayload[]; profile_id?: number }) => {
      return await addMultipleDeal(data, profile_id);
    },
    ...config
  });
};

export const useSendRequestAccess = (config = {}) => {
  const { sendRequestAccess } = useContractApi();
  return useMutation({
    mutationFn: async (data: { contract_id: number }) => {
      return await sendRequestAccess(data);
    },
    ...config
  });
};

export const useNotifyClient = (config = {}) => {
  const { notifyClient } = useContractApi();
  return useMutation({
    mutationFn: async (data: { contract_id: number }) => {
      return await notifyClient(data);
    },
    ...config
  });
};

export const useDeleteDealTask = (config = {}) => {
  const { deleteDealTask } = useContractApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dealId, taskId }: { dealId: number; taskId: number }) => {
      const res = await deleteDealTask(dealId, taskId);
      await queryClient.invalidateQueries(['my_tasks']);
      return res;
    },
    ...config
  });
};

export const useUpdateTask = (config = {}) => {
  const { updateTask } = useContractApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, taskId, data }: { dealId: number; taskId: number; data: EditTasksPayload }) => {
      const res = await updateTask(dealId, taskId, data);
      await queryClient.invalidateQueries(['my_tasks']);
      return res;
    },
    ...config
  });
};

export const useAddTasks = (config = {}) => {
  const { addTasks } = useContractApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, data }: { dealId: number; data: AddTasksPayload[] }) => {
      const res = await addTasks(dealId, data);
      await queryClient.invalidateQueries(['my_tasks']);
      return res;
    },
    ...config
  });
};

export const useCategoryDefaultTasks = (config = {}): UseQueryResult<CategoryTask[], AxiosError> => {
  const { getCategoryDefaultTasks } = useContractApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['category_default_tasks'],
    queryFn: async () => {
      try {
        const res = await getCategoryDefaultTasks();
        const defaultCategories = res
          .filter((category: CategoryTask) => category.category_root !== null)
          .map((category: CategoryTask) => ({
            categoryId: category.category_root.id,
            categoryName: category.category_root.name
          }));

        queryClient.setQueryData(['default_categories'], defaultCategories);

        return res;
      } catch (err) {
        console.error('Error occurred while fetching category default tasks');
        return [];
      }
    },
    staleTime: 1000 * 60 * 60 * 24,
    ...config
  });
};

export const useDefaultCategories = (config = {}): UseQueryResult<Record<string, string | number>[]> => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['default_categories'],
    queryFn: () => queryClient.getQueryData(['default_categories']) || [],
    staleTime: 1000 * 60 * 60 * 24,
    ...config
  });
};

export const useAddBulkDeal = (config = {}) => {
  const { addBulkDeal } = useContractApi();
  return useMutation({
    mutationFn: async (data: BulkDeal[]) => {
      return await addBulkDeal(data);
    },
    ...config
  });
};

export const useParseBulkContractFile = (config = {}) => {
  const { parseBulkContractFile } = useContractApi();
  return useMutation({
    mutationFn: async (data: any) => {
      return await parseBulkContractFile(data);
    },
    ...config
  });
};

export const useEditDeal = (config = {}) => {
  const { editDeal } = useContractApi();
  return useMutation({
    mutationFn: async (data: EditDealRequest & { dealId: number }) => {
      const { dealId, ...rest } = data;
      return await editDeal(dealId, rest);
    },
    ...config
  });
};

export const useAddDeal = (config = {}) => {
  const { addDeal } = useContractApi();
  return useMutation({
    mutationFn: async ({ data, profile_id }: { data: Deal; profile_id?: number }) => {
      return await addDeal(data, profile_id);
    },
    ...config
  });
};

export const useUpdateContractComments = (config = {}) => {
  const { updateContractComments } = useContractApi();
  return useMutation({
    mutationFn: async ({ contractId, commentsData }: { contractId: string; commentsData: string }) => {
      return await updateContractComments(contractId, commentsData);
    },
    ...config
  });
};

export const useDeal = (id: number, config = {}): UseQueryResult<Deal, AxiosError> => {
  const { fetchDeal } = useContractApi();
  return useQuery({
    queryKey: ['deal', id],
    queryFn: () => {
      return fetchDeal(id);
    },
    ...config
  });
};
